/**
 * Business Details tab for onboarding
 * Allows users to review and complete business information
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Building, Save, CheckCircle, AlertCircle } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus, BusinessDetailsFormData } from '@/types/onboarding';

// Validation schema - Allow empty strings for saving progress
const businessDetailsSchema = z.object({
  restaurant_name: z.string().optional(),
  organisation_name: z.string().optional(),
  address: z.string().optional(),
  email: z.string().email('Valid email is required').optional().or(z.literal('')),
  phone: z.string().optional(),
  contact_person: z.string().optional(),
  nzbn: z.string().optional(),
  company_name: z.string().optional(),
  trading_name: z.string().optional(),
  director_name: z.string().optional(),
  director_mobile_number: z.string().optional(),
  gst_number: z.string().optional(),
  venue_operating_hours: z.string().optional(),
  default_preparation_time_minutes: z.number().min(1).max(120).optional(),
  facebook_url: z.string().optional(),
  instagram_url: z.string().optional(),
  google_review_url: z.string().optional(),
  ubereats_markup: z.number().min(0).max(100).optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
});

// Required fields for completion (but not for saving)
const REQUIRED_FIELDS = [
  'restaurant_name',
  'organisation_name', 
  'address',
  'email',
  'phone',
  'contact_person',
  'nzbn',
  'company_name',
  'trading_name',
  'director_name',
  'director_mobile_number',
  'gst_number'
] as const;

type BusinessDetailsFormValues = z.infer<typeof businessDetailsSchema>;

const BusinessDetailsTab: React.FC = () => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    validateSection,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sectionStatus = getSectionStatus(OnboardingSection.BUSINESS_DETAILS);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.BUSINESS_DETAILS), [onboardingData]);

  const form = useForm<BusinessDetailsFormValues>({
    resolver: zodResolver(businessDetailsSchema),
    defaultValues: {
      restaurant_name: '',
      organisation_name: '',
      address: '',
      email: '',
      phone: '',
      contact_person: '',
      nzbn: '',
      company_name: '',
      trading_name: '',
      director_name: '',
      director_mobile_number: '',
      gst_number: '',
      venue_operating_hours: '',
      default_preparation_time_minutes: 15,
      facebook_url: '',
      instagram_url: '',
      google_review_url: '',
      ubereats_markup: undefined,
      primary_color: '',
      secondary_color: '',
    },
  });

  // Load existing data when component mounts or data changes
  useEffect(() => {
    if (sectionData && Object.keys(sectionData).length > 0) {
      const formData: Partial<BusinessDetailsFormValues> = {};
      
      Object.keys(sectionData).forEach(key => {
        const value = sectionData[key];
        if (value !== null && value !== undefined) {
          formData[key as keyof BusinessDetailsFormValues] = value;
        }
      });

      form.reset(formData);
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Check if all required fields are filled for completion
  const checkIfComplete = (): boolean => {
    const formValues = form.getValues();
    return REQUIRED_FIELDS.every(field => {
      const value = formValues[field];
      return value !== null && value !== undefined && value !== '';
    });
  };

  const isComplete = checkIfComplete();

  // Check if field was pre-filled by sales team
  const isPreFilled = (fieldName: keyof BusinessDetailsFormValues): boolean => {
    return sectionData?.[fieldName] !== null && 
           sectionData?.[fieldName] !== undefined && 
           sectionData?.[fieldName] !== '';
  };

  // Save section data
  const handleSave = async (data: BusinessDetailsFormValues) => {
    try {
      await updateSection(OnboardingSection.BUSINESS_DETAILS, data);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving business details:', error);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    if (!isComplete) return;
    
    try {
      // Save current data first
      const formData = form.getValues();
      await updateSection(OnboardingSection.BUSINESS_DETAILS, formData);
      
      // Then mark as complete
      await markSectionComplete(OnboardingSection.BUSINESS_DETAILS);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error marking section complete:', error);
    }
  };

  // Status badge component
  const StatusBadge = () => {
    switch (sectionStatus) {
      case OnboardingStatus.COMPLETE:
        return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Business Details</h2>
            <p className="text-sm text-muted-foreground">
              Review and complete your business information
            </p>
          </div>
        </div>
        <StatusBadge />
      </div>

      {/* Status alerts */}
      {sectionStatus === OnboardingStatus.NEEDS_ATTENTION && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This section needs your attention. Please review the feedback from our team and make the necessary changes.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.AWAITING_CONFIRMATION && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Your business details have been submitted for review. Our team will verify the information and contact you if any changes are needed.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.COMPLETE && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Your business details have been verified and approved.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Core details about your restaurant and organisation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="restaurant_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Restaurant Name <span className="text-red-500">*</span>
                        {isPreFilled('restaurant_name') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('restaurant_name') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="e.g., Bob's Burgers Wellington"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="organisation_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Organisation Name
                        {isPreFilled('organisation_name') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('organisation_name') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="e.g., Bob's Burgers"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Business Address
                      {isPreFilled('address') && (
                        <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className={isPreFilled('address') ? 'bg-blue-50 border-blue-200' : ''}
                        placeholder="Full business address including city and postal code"
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Contact Email
                        {isPreFilled('email') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          className={isPreFilled('email') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="contact@restaurant.com"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Phone Number
                        {isPreFilled('phone') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('phone') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="+64 9 XXX XXXX"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="contact_person"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Primary Contact Person
                      {isPreFilled('contact_person') && (
                        <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className={isPreFilled('contact_person') ? 'bg-blue-50 border-blue-200' : ''}
                        placeholder="Full name of primary contact"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Legal Information */}
          <Card>
            <CardHeader>
              <CardTitle>Legal Information</CardTitle>
              <CardDescription>
                Company registration and tax details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="nzbn"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        NZBN
                        {isPreFilled('nzbn') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('nzbn') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="New Zealand Business Number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gst_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        GST Number
                        {isPreFilled('gst_number') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('gst_number') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="123456789"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Registered Company Name
                        {isPreFilled('company_name') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('company_name') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="Official company name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="trading_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Trading Name
                        {isPreFilled('trading_name') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('trading_name') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="Business trading name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="director_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Director Name
                        {isPreFilled('director_name') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('director_name') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="Company director full name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="director_mobile_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Director Mobile
                        {isPreFilled('director_mobile_number') && (
                          <Badge variant="outline" className="text-xs">Pre-filled</Badge>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className={isPreFilled('director_mobile_number') ? 'bg-blue-50 border-blue-200' : ''}
                          placeholder="Director mobile number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Operational Details */}
          <Card>
            <CardHeader>
              <CardTitle>Operational Details</CardTitle>
              <CardDescription>
                Optional information about your operations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="venue_operating_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Operating Hours</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="e.g., Mon-Thu: 11:00-22:00, Fri-Sat: 11:00-23:00, Sun: 12:00-21:00"
                        rows={3}
                      />
                    </FormControl>
                    <FormDescription>
                      Describe your typical operating hours
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="default_preparation_time_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Preparation Time (minutes)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min="1"
                        max="120"
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 15)}
                      />
                    </FormControl>
                    <FormDescription>
                      How long customers should expect to wait for their order
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Social Media Links */}
          <Card>
            <CardHeader>
              <CardTitle>Social Media & Reviews</CardTitle>
              <CardDescription>
                Optional social media and review links
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="facebook_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Facebook Page URL</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="www.facebook.com/yourrestaurant"
                        />
                      </FormControl>
                      <FormDescription>
                        Can be shortened (e.g., www.facebook.com/yourpage) for SMS use
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instagram_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram Profile URL</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="www.instagram.com/yourrestaurant"
                        />
                      </FormControl>
                      <FormDescription>
                        Can be shortened (e.g., www.instagram.com/yourpage) for SMS use
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="google_review_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Google Review URL</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="g.page/r/..."
                      />
                    </FormControl>
                    <FormDescription>
                      Direct link for customers to leave Google reviews. Can be shortened (e.g., g.page/r/...) for SMS use
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Delivery & Branding Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery & Branding Settings</CardTitle>
              <CardDescription>
                Optional settings for delivery platforms and brand customisation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="ubereats_markup"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UberEats Markup (%)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        placeholder="0.0"
                      />
                    </FormControl>
                    <FormDescription>
                      Markup percentage applied to menu items on UberEats (optional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Brand Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <div 
                            className="w-10 h-10 rounded-md border border-gray-300"
                            style={{ backgroundColor: field.value || '#4093ff' }}
                          />
                          <Input
                            {...field}
                            placeholder="#4093ff"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Primary color used in branding and UI elements
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="secondary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Brand Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <div 
                            className="w-10 h-10 rounded-md border border-gray-300"
                            style={{ backgroundColor: field.value || '#ffffff' }}
                          />
                          <Input
                            {...field}
                            placeholder="#000000"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Secondary color used for accents and contrasts
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Progress message */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You can save your progress at any time. Fields marked with <span className="text-red-500">*</span> are required to complete this section, but you don't need to fill them all at once.
              </p>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <div className="text-sm text-amber-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  You have unsaved changes
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                type="submit"
                variant="outline"
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save Progress'}
              </Button>

              <Button
                type="button"
                onClick={handleMarkComplete}
                disabled={!isComplete || isMarkingComplete || hasUnsavedChanges}
                className="flex items-center gap-2"
                title={!isComplete ? 'Please fill all required fields before marking as complete' : ''}
              >
                <CheckCircle className="h-4 w-4" />
                {isMarkingComplete ? 'Submitting...' : 'Mark as Complete'}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessDetailsTab;